// src/scenes/purchases/EditPurchase.jsx
import * as React from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box, Button, Grid, TextField, MenuItem, Paper,
  Typography, CircularProgress, Snackbar, Alert, IconButton
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Header from "../../components/Header";
import axios from "axios";

const api = axios.create({ baseURL: process.env.REACT_APP_API_URL });
const get = (p) => api.get(p, { params: { _t: Date.now() } }).then(r => r.data);
const put = (p, b) => api.put(p, b).then(r => r.data);

const fmt = (v) =>
  v == null || v === "" ? "0.00" : Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function EditPurchase() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = React.useState(null); // main editable object
  const [options, setOptions] = React.useState({ suppliers: [], items: [], warehouses: [], cars: [] });
  const [calc, setCalc] = React.useState({});
  const [loading, setLoading] = React.useState(true);
  const [snack, setSnack] = React.useState({ open: false, severity: "success", message: "" });

  // ---------- LOAD DATA ----------
  React.useEffect(() => {
    let mounted = true;
    setLoading(true);
    Promise.all([
      get(`/api/purchase/${id}`),
      get("/api/customer?type=supplier"),
      get("/api/item"),
      get("/api/warehouse"),
      get("/api/carinfo")
    ]).then(([purchaseRes, s, i, w, c]) => {
      if (!mounted) return;

      const purchase = purchaseRes.data;


      // Normalize items: API may return PurchaseItems or items
      const rawItems = Array.isArray(purchase?.PurchaseItems) && purchase.PurchaseItems.length
        ? purchase.PurchaseItems
        : Array.isArray(purchase?.items)
          ? purchase.items
          : [];

      const normalizedItems = rawItems.map(pi => ({
        // keep optional id for the PurchaseItem record if needed
        id: pi.id ?? pi.purchaseItemId ?? null,
        itemId: pi.itemId ?? pi.Item?.id ?? null,
       quantity: pi.quantity ?? 0, 
        unitPrice: pi.unitPrice ? Number(pi.unitPrice) : (pi.unitPrice ?? 0),
        bonus: pi.bonus ?? 0,
        sponsor: pi.sponsor ?? null
      }));

      const normalizedPurchase = {
        // basic top-level fields user expects to edit
        id: purchase?.id ?? null,
        supplierId: purchase.Customer?.id ?? "",
        warehouseId: purchase.Warehouse?.id ?? "",
        fsNo: purchase.fsNo ?? "",
        carId: purchase?.carId ?? purchase?.CarId ?? "",
        purchaseDate: purchase?.purchaseDate ? purchase.purchaseDate.split("T")[0] : new Date().toISOString().split("T")[0],
        priceDiscount: Number(purchase?.priceDiscount ?? 0),
        specialSalesDiscount: Number(purchase?.specialSalesDiscount ?? 0),
        serviceCharge: Number(purchase?.serviceCharge ?? 0),
        fixedPriceDiscount: Number(purchase?.fixedPriceDiscount ?? 0),
        status: purchase?.status ?? "pending",
        items: normalizedItems.length ? normalizedItems : [{ itemId: "", quantity: 0, unitPrice: 0, bonus: 0, sponsor: "" }]
      };

      setOptions({
        suppliers: Array.isArray(s) ? s : (s?.data ?? []),
        items: Array.isArray(i) ? i : (i?.data ?? []),
        warehouses: Array.isArray(w) ? w : (w?.data ?? []),
        cars: Array.isArray(c) ? c : (c?.data ?? [])
      });

      setData(normalizedPurchase);
      setLoading(false);
    }).catch(err => {
      console.error("Failed to load purchase/edit options:", err);
      setSnack({ open: true, severity: "error", message: "Failed to load data" });
      setLoading(false);
    });

    return () => { mounted = false; };
  }, [id]);

  // ---------- CALCULATION ----------
  const recalculate = React.useCallback(() => {
    if (!data) return;

    // compute subtotal across items
    const subTotal = (data.items || []).reduce((s, it) => {
      const qty = Number(it.quantity) || 0;
      const price = Number(it.unitPrice) || 0;
      return s + qty * price;
    }, 0);

    // excise per item using master items' ExciseTax
    const exciseAmt = (data.items || []).reduce((s, it) => {
      const qty = Number(it.quantity) || 0;
      const price = Number(it.unitPrice) || 0;
      const master = options.items.find(o => Number(o.id) === Number(it.itemId));
      const exciseRate = (master?.ExciseTax ?? 0) / 100;
      return s + qty * price * exciseRate;
    }, 0);

    const afterExcise = subTotal + exciseAmt;
    const disc1 = Number(data.priceDiscount || 0);
    const disc2 = Number(data.specialSalesDiscount || 0);
    const charge = Number(data.serviceCharge || 0);
    const fixedDisc = Number(data.fixedPriceDiscount || 0);

    const beforeVat = afterExcise - disc1 - disc2 + charge;
    const vat = beforeVat * 0.15;
    const withVat = beforeVat * 1.15;
    const afterDisc = withVat - fixedDisc;

    // withholding formula (same as earlier): apply on (subTotal + charge - discounts - fixedDisc)
    const withhold = (subTotal + charge - (disc1 + disc2 + fixedDisc)) * 0.03;

    setCalc({
      subTotal: subTotal.toFixed(4),
      exciseTaxedPrice: exciseAmt.toFixed(4),
      priceAfterExice: afterExcise.toFixed(4),
      totalBeforeVat: beforeVat.toFixed(4),
      vat: vat.toFixed(4),
      totalWithVat: withVat.toFixed(4),
      totalVatedAfterDiscount: afterDisc.toFixed(4),
      withholdingAmount: withhold.toFixed(4),
    });
  }, [data, options.items]);

  // recalc on data or options change
  React.useEffect(() => { recalculate(); }, [data, recalculate]);

  // ---------- ITEM HELPERS ----------
  const updateItem = (idx, changes) => {
    setData(prev => {
      const items = Array.isArray(prev.items) ? prev.items.slice() : [];
      items[idx] = { ...items[idx], ...changes };
      return { ...prev, items };
    });
  };

  const addItem = () => {
    setData(prev => ({ ...prev, items: [...(prev.items || []), { itemId: "", quantity: 0, unitPrice: 0, bonus: 0, sponsor: "" }] }));
  };

  const removeItem = (idx) => {
    setData(prev => {
      const items = (prev.items || []).slice();
      items.splice(idx, 1);
      return { ...prev, items: items.length ? items : [{ itemId: "", quantity: 0, unitPrice: 0, bonus: 0, sponsor: "" }] };
    });
  };

  // ---------- SAVE ----------
  const save = async () => {
    if (!data) return;
    try {
      setLoading(true);

      // build totals from calc (numbers)
      const totals = {
        subTotal: Number(calc.subTotal || 0),
        exciseTaxedPrice: Number(calc.exciseTaxedPrice || 0),
        priceAfterExice: Number(calc.priceAfterExice || 0),
        totalBeforeVat: Number(calc.totalBeforeVat || 0),
        vat: Number(calc.vat || 0),
        totalWithVat: Number(calc.totalWithVat || 0),
        totalVatedAfterDiscount: Number(calc.totalVatedAfterDiscount || 0),
        withholdingAmount: Number(calc.withholdingAmount || 0),
      };

      // payload uses "items" as in your PUT example
      const payload = {
        supplierId: Number(data.supplierId),
        warehouseId: Number(data.warehouseId),
        carId: data.carId ? Number(data.carId) : null,
        subTotal: totals.subTotal,
        exciseTaxedPrice: totals.exciseTaxedPrice,
        priceAfterExice: totals.priceAfterExice,
        priceDiscount: Number(data.priceDiscount || 0),
        specialSalesDiscount: Number(data.specialSalesDiscount || 0),
        serviceCharge: Number(data.serviceCharge || 0),
        totalBeforeVat: totals.totalBeforeVat,
        totalWithVat: totals.totalWithVat,
        fixedPriceDiscount: Number(data.fixedPriceDiscount || 0),
        totalVatedAfterDiscount: totals.totalVatedAfterDiscount,
        withholdingAmount: totals.withholdingAmount,
        purchaseDate: data.purchaseDate,
        fsNo: data.fsNo || null,
        items: (data.items || []).map(it => ({
          itemId: Number(it.itemId),
          quantity: Number(it.quantity),
          unitPrice: Number(it.unitPrice),
          bonus: Number(it.bonus || 0),
          sponsor: it.sponsor ?? null
        }))
      };

      await put(`/api/purchase/${id}`, payload);

      setSnack({ open: true, severity: "success", message: "Purchase updated successfully!" });
      setTimeout(() => navigate("/purchase/view"), 900);
    } catch (err) {
      console.error("Save failed:", err);
      setSnack({ open: true, severity: "error", message: "Failed to save purchase" });
    } finally {
      setLoading(false);
    }
  };

  // ---------- RENDER ----------
  if (loading || !data) return <Box m="20px"><CircularProgress /></Box>;

  // field descriptors you used originally (kept the same order)
  const fields = [
    { label: "Supplier", field: "supplierId", type: "select", options: options.suppliers, key: "name" },
    // item select moved to the items table below (we no longer show single Item here)
    { label: "Warehouse", field: "warehouseId", type: "select", options: options.warehouses, key: "name" },


    { label: "FS No", field: "fsNo", type: "text" },
    { label: "Description", field: "description", type: "text" },
    { label: "Purchase Date", field: "purchaseDate", type: "date" },
  ];

  return (
    <Box m="20px">
      <Header title="EDIT PURCHASE" />

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Grid container spacing={2}>
              {/* Top fields: Supplier, Warehouse, Car, Discounts, FS No, Date */}
              {fields.map(f => (
                <Grid item xs={12} sm={6} key={f.field}>
                  {f.type === "select" ? (
                    <TextField
                      select
                      fullWidth
                      label={f.label}
                      value={data[f.field] ?? ""}
                      onChange={e => setData(d => ({ ...d, [f.field]: e.target.value }))}
                      variant="filled"
                    >
                      {(f.options || []).map(o => (
                        <MenuItem key={o.id} value={o.id}>
                          {f.display ? f.display(o) : `${o[f.key] ?? o.name ?? o.companyName ?? `#${o.id}`}${f.suffix?.(o) || ""}`}
                        </MenuItem>
                      ))}
                    </TextField>
                  ) : (
                    <TextField
                      fullWidth
                      label={f.label}
                      type={f.type === "date" ? "date" : (f.type === "number" ? "number" : "text")}
                      inputProps={f.step ? { step: f.step } : undefined}
                      value={data[f.field] ?? (f.type === "date" ? new Date().toISOString().split("T")[0] : "")}
                      onChange={e => setData(d => ({ ...d, [f.field]: f.type === "number" ? e.target.value : e.target.value }))}
                      variant="filled"
                      InputLabelProps={f.type === "date" ? { shrink: true } : undefined}
                    />
                  )}
                </Grid>
              ))}

              {/* Items table/editor */}
              <Grid item xs={12}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>Items</Typography>
                  <Button startIcon={<AddIcon />} variant="outlined" onClick={addItem}>Add Item</Button>
                </Box>

                {(data.items || []).map((it, idx) => {
                  const rowTotal = (Number(it.quantity) || 0) * (Number(it.unitPrice) || 0);
                  return (
                    <Paper key={idx} sx={{ p: 1, mb: 1 }}>
                      <Grid container spacing={1} alignItems="center">
                        <Grid item xs={12} md={5}>
                          <TextField
                            select
                            fullWidth
                            label="Item"
                            value={it.itemId ?? ""}
                            onChange={e => updateItem(idx, { itemId: e.target.value })}
                            variant="filled"
                          >
                            {options.items.map(o => (
                              <MenuItem key={o.id} value={o.id}>
                                {o.name} {o.ExciseTax ? `(${o.ExciseTax}%)` : ""}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>

                        <Grid item xs={6} md={1.4}>
                          <TextField
                            fullWidth
                            label="Qty"
                            type="number"
                            value={it.quantity}
                            onChange={e => updateItem(idx, { quantity: e.target.value })}
                            variant="filled"
                            inputProps={{ min: 0, step: "1" }}
                          />
                        </Grid>

                        <Grid item xs={6} md={1.6}>
                          <TextField
                            fullWidth
                            label="Unit Price"
                            type="number"
                            value={it.unitPrice}
                            onChange={e => updateItem(idx, { unitPrice: e.target.value })}
                            variant="filled"
                            inputProps={{ min: 0, step: "0.01" }}
                          />
                        </Grid>

                        <Grid item xs={6} md={1.5}>
                          <TextField
                            fullWidth
                            label="Bonus"
                            type="number"
                            value={it.bonus}
                            onChange={e => updateItem(idx, { bonus: e.target.value })}
                            variant="filled"
                            inputProps={{ min: 0, step: "1" }}
                          />
                        </Grid>

                        <Grid item xs={6} md={1.5}>
                          <TextField
                            fullWidth
                            label="Sponsor"
                            value={it.sponsor ?? ""}
                            onChange={e => updateItem(idx, { sponsor: e.target.value })}
                            variant="filled"
                          />
                        </Grid>

                        <Grid item xs={6} md={1}>
                          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                            {fmt(rowTotal)}
                          </Typography>
                        </Grid>

                        <Grid item xs={6} md={0.5}>
                          <IconButton aria-label="remove" color="error" onClick={() => removeItem(idx)}>
                            <DeleteOutlineIcon />
                          </IconButton>
                        </Grid>
                      </Grid>
                    </Paper>
                  );
                })}
              </Grid>

            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <div style={{ padding: "16px", background: "#fafafa", borderRadius: 8 }}>
            <h3 style={{ textAlign: "center", color: "#1976d2", marginBottom: "16px" }}>Purchase Details</h3>

            <dl style={{ margin: 0 }}>
              {[
                { label: "Supplier", value: options.suppliers.find(s => s.id === Number(data.supplierId))?.name ?? "-" },
                { label: "Warehouse", value: options.warehouses.find(w => w.id === Number(data.warehouseId))?.name ?? "-" },
                
                { label: "Subtotal", value: calc.subTotal },
               
                { label: "Description", value: data.description ?? "—" },
                
                { label: "FS No", value: data.fsNo ?? "—" },
                { label: "Status", value: data.status ?? "—" },
                { label: "Purchase Date", value: data.purchaseDate ? new Date(data.purchaseDate).toLocaleString() : "—" },
              ].map(row => (
                <div key={row.label} style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                  <span style={{ color: "#555" }}>{row.label}</span>
                  <span style={{ fontWeight: "bold", color: row.label.includes("Total") ? "#1976d2" : row.label.includes("Discount") ? "#ff9800" : "#000" }}>
                    {Number(row.value) || Number(row.value) === 0
                      ? Number(row.value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                      : row.value}
                  </span>
                </div>
              ))}
            </dl>

            <div style={{ textAlign: "center", marginTop: 16, fontWeight: "bold", color: "#1976d2" }}>
              {Number(calc.totalVatedAfterDiscount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ETB
            </div>
          </div>
        </Grid>

      </Grid>

      <Box mt={1} display="flex" gap={2}>
        <Button variant="contained" color="secondary" onClick={save}>Save Changes</Button>
        <Button variant="outlined" onClick={() => navigate("/purchase/view")}>Cancel</Button>
      </Box>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ mt: 5 }}
      >
        <Alert onClose={() => setSnack((s) => ({ ...s, open: false }))} severity={snack.severity} sx={{ width: "100%" }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
